import React from 'react';
import Chart from 'chart.js/auto';

const ChartComponent = ({ chartData }) => {

  const chartRef = React.useRef(null);
  const chartInstance = React.useRef(null);

  const COMPANY_THEME = {

    
    TATA: {
      border: '#C8102E',
      bg: 'rgba(200,16,46,0.08)',
      dot: '#C8102E',
      isTata: true
    },


    HDFC: {
      border: '#16a34a',
      bg: 'rgba(22,163,74,0.08)',
      dot: '#16a34a'
    }

  };

  const getCompanyType = (comp) => {

    if (comp?.tabName) {
      return comp.tabName.toUpperCase();
    }

    if (comp?.companyName) {
      return comp.companyName.toUpperCase();
    }

    if (comp?.name?.includes('Competitor T')) {
      return 'TATA';
    }

    if (comp?.name?.includes('Competitor H')) {
      return 'HDFC';
    }

    return 'ICICI';
  };

  React.useEffect(() => {

    if (!chartData?.comparisons?.length) {
      return;
    }

    const ctx =
      chartRef.current.getContext('2d');

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // DATASETS
    const datasets =
      (chartData.comparisons || [])
        .filter(comp =>
          Array.isArray(comp.values) &&
          comp.values.length > 0
        )
        .map((comp) => {

          const companyType =
            getCompanyType(comp);

          // THEME
          const theme =
            COMPANY_THEME[companyType] || {};

          // DEFAULT → BLUE
          const border =
            theme.border || '#1A2B5F';

          const bg =
            theme.bg ||
            'rgba(26,43,95,0.08)';

          return {

            label: comp.name,

            data: comp.values.map(v =>
              Number(v || 0)
            ),

            borderColor: border,

            backgroundColor: bg,

            borderWidth: 2,

            borderDash: [],

            tension: 0.35,

            fill: false,

            pointRadius: 4,

            pointHoverRadius: 6,
          };
        });

    // BEST FUND
    if (chartData.bestTataFund) {

      datasets.push({

        label: "Best Fund",

        data:
          chartData.bestTataFund.values || [],

        borderColor: "#d97706",

        borderDash: [6, 4],

        borderWidth: 2,

        tension: 0.35,

        fill: false,

        pointRadius: 4,

        pointHoverRadius: 6,
      });
    }

    chartInstance.current = new Chart(ctx, {

      type: "line",

      data: {
        labels: chartData.years,
        datasets
      },

      options: {

        responsive: true,

        maintainAspectRatio: false,

        interaction: {
          mode: "index",
          intersect: false
        },

        plugins: {
          legend: {
            display: false
          }
        },

        scales: {

          y: {

            title: {
              display: true,
              text: "Fund Value (₹)"
            },

            ticks: {

              stepSize: 50000000,

              callback: function (value) {

                if (value === 0) {
                  return "₹0";
                }

                return (
                  "₹" +
                  (value / 10000000).toFixed(1) +
                  "Cr"
                );
              }
            }
          },

          x: {
            title: {
              display: true,
              text: "Policy Year"
            }
          }
        }
      }
    });

    return () => {

      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };

  }, [chartData]);

  const formatCurrency = (v) => {

    if (v >= 10000000) {
      return (
        "₹" +
        (v / 10000000).toFixed(2) +
        " Cr"
      );
    }

    if (v >= 100000) {
      return (
        "₹" +
        (v / 100000).toFixed(2) +
        " L"
      );
    }

    return "₹" + v.toLocaleString("en-IN");
  };

  if (!chartData) return null;

  const sa =
    chartData.sumAssured >= 100000
      ? (
          chartData.sumAssured / 100000
        ).toFixed(0) + "L"
      : chartData.sumAssured;

  return (
    <div className="chart-card">

      <div className="chart-title">
        Fund Value Comparison
      </div>

      <div className="chart-subtitle">
        Age {chartData.age}
        {" | "}
        SA ₹{sa}
        {" | "}
        Policy Term {chartData.years.length} years
      </div>

      {/* LEGEND */}
      <div className="legend-row">

        {(chartData.comparisons || []).map((comp) => {

          const companyType =
            getCompanyType(comp);

          const theme =
            COMPANY_THEME[companyType] || {};

          const dotColor =
            theme.dot || '#1A2B5F';

          return (

            <div
              key={comp.name}
              className="legend-item"
            >

              <div
                className="legend-dot"
                style={{
                  backgroundColor: dotColor
                }}
              ></div>

              {comp.name}

            </div>
          );
        })}

        {/* BEST FUND */}
        {chartData.bestTataFund && (

          <div className="legend-item">

            <div
              className="legend-dot"
              style={{
                backgroundColor: '#d97706'
              }}
            ></div>

            Best TATA Fund

          </div>

        )}

      </div>

      {/* CHART */}
      <div className="chart-outer">

        <div className="chart-wrap">
          <canvas ref={chartRef}></canvas>
        </div>

      </div>

      <div className="endlabels-panel">

        {(chartData.comparisons || []).map((comp) => {

          const companyType =
            getCompanyType(comp);

          const theme =
            COMPANY_THEME[companyType] || {};

          const borderColor =
            theme.border || '#1A2B5F';

          const bgColor =
            theme.bg ||
            'rgba(26,43,95,0.08)';

          return (

            <div
              key={comp.name}
              className="endlabel-row"

              style={{
                background: bgColor,

                borderLeft:
                  `4px solid ${borderColor}`
              }}
            >

              <div className="endlabel-fund">
                {comp.name}
              </div>

              <div
                className="endlabel-val"
                style={{
                  color: borderColor
                }}
              >
                {formatCurrency(
                  comp.finalYearValue
                )}
              </div>

              <div className="endlabel-growth">
                Final Value
              </div>

            </div>
          );
        })}

        {/* BEST FUND */}
        {chartData.bestTataFund && (

          <div
            className="endlabel-row"
            style={{
              background:
                'rgba(217,119,6,0.08)',

              borderLeft:
                '4px solid #d97706'
            }}
          >

            <div className="endlabel-fund">
              ⭐ Best TATA Fund
            </div>

            <div
              className="endlabel-val"
              style={{
                color: '#d97706'
              }}
            >
              {formatCurrency(
                chartData.bestTataFund
                  .finalYearValue
              )}
            </div>

            <div className="endlabel-growth">
              Final Value
            </div>

          </div>
        )}

      </div>

      <div className="info-row">
        &#8505;&#65039;&nbsp;
        Fund values are illustrative projections
        based on historical returns.
      </div>

    </div>
  );
};

export default ChartComponent;